import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, orderBy, query } from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebase';
import { getPatternLabel } from '../data/bingoPatterns';
import './EventReports.css';

const FILTERS = {
  all: null,
  calls: ['BALL_CALLED', 'BALL_UNDONE'],
  verifications: ['CARD_VERIFIED'],
  winners: ['WINNER_CONFIRMED'],
  games: ['GAME_STARTED', 'GAME_COMPLETED'],
};

const toDate = (value) => value?.toDate?.() || (value ? new Date(value) : null);
const formatTime = (value) => {
  const date = toDate(value);
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' }) : '—';
};
const formatDate = (value) => {
  const date = toDate(value);
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
};
const formatDuration = (startValue, endValue) => {
  const start = toDate(startValue);
  const end = toDate(endValue);
  if (!start || !end) return '—';
  const totalSeconds = Math.max(0, Math.round((end.getTime() - start.getTime()) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
};
const getEventLabel = (entry) => ({
  GAME_STARTED: entry.gameName || `Game ${entry.gameNumber || ''}`,
  BALL_CALLED: entry.payload?.ball,
  BALL_UNDONE: `${entry.payload?.ball || ''} removed`,
  CARD_VERIFIED: entry.payload?.result,
  WINNER_CONFIRMED: entry.payload?.cardIdentifier || 'Winner confirmed',
  GAME_COMPLETED: `${entry.payload?.finalCalledBallCount ?? 0} final calls`,
}[entry.type] || '');

function EventReports() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [audit, setAudit] = useState([]);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const eventRef = doc(db, 'bingoMachineEvents', eventId);
        const [eventSnapshot, auditSnapshot] = await Promise.all([
          getDoc(eventRef),
          getDocs(query(collection(eventRef, 'auditEvents'), orderBy('timestamp', 'asc'))),
        ]);
        if (!eventSnapshot.exists()) throw new Error('Event not found.');
        if (!mounted) return;
        setEvent({ id: eventSnapshot.id, ...eventSnapshot.data() });
        setAudit(auditSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      } catch (loadError) {
        if (mounted) setError(loadError.message || 'Unable to load this report.');
      } finally { if (mounted) setLoading(false); }
    };
    load();
    return () => { mounted = false; };
  }, [eventId]);

  const games = Array.isArray(event?.gameSchedule) ? event.gameSchedule : [];
  const summary = useMemo(() => {
    const count = (type) => audit.filter((entry) => entry.type === type).length;
    return {
      gamesScheduled: games.length,
      gamesPlayed: count('GAME_STARTED'),
      gamesCompleted: count('GAME_COMPLETED'),
      callActions: count('BALL_CALLED'),
      undos: count('BALL_UNDONE'),
      verifications: count('CARD_VERIFIED'),
      winningChecks: audit.filter((entry) => entry.type === 'CARD_VERIFIED' && entry.payload?.result === 'WINNER').length,
      nonWinningChecks: audit.filter((entry) => entry.type === 'CARD_VERIFIED' && entry.payload?.result === 'NOT_WINNER').length,
      winners: count('WINNER_CONFIRMED'),
      totalPrizes: audit.filter((entry) => entry.type === 'GAME_COMPLETED').reduce((sum, entry) => sum + (Number(entry.prizeAmount) || 0), 0),
    };
  }, [audit, games.length]);

  const filteredAudit = FILTERS[filter]
    ? audit.filter((entry) => FILTERS[filter].includes(entry.type))
    : audit;

  if (loading) return <main className="reports-page"><p className="reports-status">Loading report…</p></main>;
  if (error) return <main className="reports-page"><p className="reports-status reports-error">{error}</p></main>;

  return (
    <main className="reports-page">
      <div className="reports-shell">
        <header className="reports-header">
          <div><span className="machine-eyebrow">REPORTS & AUDIT TRAIL</span><h1>{event.eventName || event.id}</h1><p>{formatDate(event.createdAt)} · {summary.gamesCompleted === games.length && games.length ? 'Completed' : 'Active / In Progress'}</p></div>
          <div className="reports-actions"><button onClick={() => window.print()}>Print / Save PDF</button><button onClick={() => navigate(`/bingo-machine?eventId=${encodeURIComponent(eventId)}`)}>Back to Event</button></div>
        </header>

        <section className="reports-summary">
          <article><span>Games Played</span><strong>{summary.gamesPlayed}</strong><small>{summary.gamesCompleted} completed / {summary.gamesScheduled} scheduled</small></article>
          <article><span>Call Actions</span><strong>{summary.callActions}</strong><small>{summary.undos} undo actions</small></article>
          <article><span>Verifications</span><strong>{summary.verifications}</strong><small>{summary.winningChecks} winner · {summary.nonWinningChecks} not winner</small></article>
          <article><span>Confirmed Winners</span><strong>{summary.winners}</strong><small>${summary.totalPrizes.toFixed(2)} completed prizes</small></article>
        </section>

        <section className="reports-section"><h2>Game Reports</h2>{games.length === 0 ? <p className="reports-empty">No scheduled games are available.</p> : <div className="reports-games">{games.map((game, index) => {
          const gameAudit = audit.filter((entry) => entry.gameId === game.id || (!entry.gameId && entry.gameNumber === (game.gameNumber || index + 1)));
          const started = gameAudit.find((entry) => entry.type === 'GAME_STARTED');
          const completed = [...gameAudit].reverse().find((entry) => entry.type === 'GAME_COMPLETED');
          const calls = gameAudit.filter((entry) => entry.type === 'BALL_CALLED');
          const verifications = gameAudit.filter((entry) => entry.type === 'CARD_VERIFIED');
          const winners = gameAudit.filter((entry) => entry.type === 'WINNER_CONFIRMED');
          const startedAt = started?.timestamp || game.startedAt;
          const completedAt = completed?.timestamp || game.completedAt;
          return <article className="reports-game" key={game.id || index}><button className="reports-game-heading" onClick={() => setExpanded((current) => ({ ...current, [index]: !current[index] }))}><span><b>Game {game.gameNumber || index + 1}</b><strong>{game.name || `Game ${index + 1}`}</strong></span><span>{game.status || (completed ? 'completed' : 'scheduled')} · {expanded[index] ? 'Hide' : 'Details'}</span></button><div className="reports-game-facts"><span>Pattern <b>{getPatternLabel(game.pattern)}</b></span><span>Prize <b>${game.prizeAmount || 0}</b></span><span>Final Calls <b>{completed?.payload?.finalCalledBallCount ?? game.generatedNumbers?.length ?? calls.length}</b></span><span>Checks <b>{verifications.length}</b></span><span>Winners <b>{winners.length}</b></span></div>{expanded[index] && <div className="reports-game-detail"><p>Started: {formatTime(startedAt)} · Completed: {formatTime(completedAt)} · Duration: {formatDuration(startedAt, completedAt)}</p><div className="reports-ball-history">{gameAudit.filter((entry) => ['BALL_CALLED', 'BALL_UNDONE'].includes(entry.type)).map((entry) => <span className={entry.type === 'BALL_UNDONE' ? 'undone' : ''} key={entry.id}>{entry.payload?.ball}{entry.type === 'BALL_UNDONE' ? ' — UNDONE' : ''}</span>)}</div><div className="reports-game-timeline">{gameAudit.filter((entry) => !['BALL_CALLED', 'BALL_UNDONE'].includes(entry.type)).map((entry) => <div key={entry.id}><time>{formatTime(entry.timestamp)}</time><b>{entry.type.replaceAll('_', ' ')}</b><span>{getEventLabel(entry)}</span></div>)}</div></div>}</article>;
        })}</div>}</section>

        <section className="reports-section"><div className="reports-section-heading"><h2>Audit Trail</h2><div className="reports-filters">{Object.keys(FILTERS).map((key) => <button className={filter === key ? 'active' : ''} onClick={() => setFilter(key)} key={key}>{key === 'all' ? 'All Activity' : key[0].toUpperCase() + key.slice(1)}</button>)}</div></div>{audit.length === 0 ? <p className="reports-empty">No audit history is available for this legacy event.</p> : <div className="reports-timeline">{filteredAudit.map((entry) => <div className="reports-timeline-row" key={entry.id}><time>{formatTime(entry.timestamp)}</time><span className={`reports-type type-${entry.type.toLowerCase()}`}>{entry.type.replaceAll('_', ' ')}</span><strong>{getEventLabel(entry)}</strong><small>{entry.gameName || (entry.gameNumber ? `Game ${entry.gameNumber}` : '')}</small></div>)}</div>}</section>
      </div>
    </main>
  );
}

export default EventReports;
